import { useState } from 'react';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import { parseStatementConflict, type StatementConflict } from '@/features/statement-configs/conflict';
import type { StatementConfigDTO } from './use-get-statement-configs';

export interface UpsertStatementConfigRequest {
  place: string;
  name: string;
  position: string;
  /**
   * The Utama's `updatedAt` this form started from, or null when it believes
   * no Utama exists yet. A colleague's newer save answers 409.
   */
  expectedUpdatedAt?: string | null;
}

export interface UpsertStatementConfigResult {
  saved: StatementConfigDTO | null;
  /** Set when the save lost a race with a colleague. */
  conflict: StatementConflict | null;
}

/**
 * Upserts the caller's Utama Pernyataan (PPJK from the token): updates the
 * active Utama, or inserts one when the PPJK has none. Used by the onboarding
 * wizard only; Master › Pernyataan edits rows by id and adds with POST.
 */
const useUpsertStatementConfig = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const upsertConfig = async (data: UpsertStatementConfigRequest): Promise<UpsertStatementConfigResult> => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.put<{ success: boolean; data: StatementConfigDTO }>(
        apiUrl.ceisa.statementConfigMe,
        data
      );
      return { saved: response.data?.data ?? null, conflict: null };
    } catch (err: unknown) {
      const maybe = err as {
        response?: { data?: { error?: { message?: string }; message?: string } };
        message?: string;
      };
      const conflict = parseStatementConflict(err);
      const errorMessage =
        conflict?.message ||
        maybe.response?.data?.error?.message ||
        maybe.response?.data?.message ||
        maybe.message ||
        'Failed to save statement';
      setError(errorMessage);
      return { saved: null, conflict };
    } finally {
      setLoading(false);
    }
  };

  return { upsertConfig, loading, error };
};

export default useUpsertStatementConfig;
